import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { theme } from "../../theme/colors";

const GUIDELINES = [
  {
    title: "Respect",
    description: "Critique ideas, not people. No harassment, hate, or threats.",
  },
  {
    title: "Stay civic",
    description: "Keep posts about community issues, public decisions, or participation.",
  },
  {
    title: "Be constructive",
    description: "Say what you're proposing and what you're asking others to do.",
  },
  {
    title: "Honesty and privacy",
    description:
      "Don't impersonate others or misrepresent your identity. No private addresses, phone numbers, documents, or screenshots.",
  },
  {
    title: "No spam",
    description:
      "No repetitive promos, fundraising links without context, or off-topic marketing.",
  },
];

export default function CommunityGuidelinesModal({
  open,
  onClose,
  onAccept,
  loading = false,
  isPolitician = false,
}) {
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    if (!open) setAgreed(false);
  }, [open]);

  const handleClose = () => {
    setAgreed(false);
    onClose?.();
  };

  const handleAccept = () => {
    if (agreed && onAccept) {
      onAccept();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { borderRadius: "16px", maxHeight: "90vh" } }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          pb: 1.5,
          borderBottom: "1px solid #e5e7eb",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <ShieldOutlinedIcon sx={{ fontSize: 28, color: theme.primary }} />
          <Typography component="span" sx={{ fontWeight: 700, fontSize: "22px", color: "#0f172a" }}>
            Community Guidelines
          </Typography>
        </Box>
        <IconButton aria-label="close" onClick={handleClose} sx={{ color: "#64748b" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, pt: 3, pb: 1 }}>
        <Typography sx={{ fontSize: 15, color: "#0f172a", lineHeight: 1.5, mt: 1, mb: 2.5 }}>
          Agree to these guidelines before your first post. Help us keep CivixThread constructive
          and trustworthy.
        </Typography>

        <Box sx={{ mb: 2.5 }}>
          {GUIDELINES.map((g, idx) => (
            <Box key={idx} sx={{ display: "flex", gap: 1.5, mb: 1.5, alignItems: "flex-start" }}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: theme.primary,
                  mt: 1.25,
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ fontSize: 15, color: "#0f172a", lineHeight: 1.47 }}>
                <Typography component="span" sx={{ fontWeight: 600 }}>
                  {g.title}:
                </Typography>{" "}
                {g.description}
              </Typography>
            </Box>
          ))}
        </Box>

        {isPolitician && (
          <Typography sx={{ fontSize: 14, color: "#334155", lineHeight: 1.47, mb: 2.5 }}>
            As an official, you can use <strong>Policy I Created</strong> for legislation you
            authored and <strong>Recent Win</strong> for constituent accomplishments. Link to
            official sources when available.
          </Typography>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.25,
            bgcolor: "#fef3c7",
            borderRadius: 1,
            p: 1.5,
            mb: 2,
          }}
        >
          <WarningAmberOutlinedIcon sx={{ fontSize: 20, color: "#0f172a", flexShrink: 0, mt: 0.25 }} />
          <Typography sx={{ flex: 1, fontSize: 14, color: "#0f172a", lineHeight: 1.47 }}>
            Report violations from the menu on any post or comment. You can block users. Repeated
            violations may limit posting access.
          </Typography>
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              sx={{
                color: theme.primary,
                "&.Mui-checked": { color: theme.primary },
              }}
            />
          }
          label={
            <Typography sx={{ fontWeight: 500, fontSize: 15, color: "#0f172a" }}>
              I agree to follow these guidelines.
            </Typography>
          }
          sx={{ m: 0, mb: 1 }}
        />
      </DialogContent>

      {/* Buttons - Cancel + I Agree & Continue like mobile */}
      <DialogActions
        sx={{
          p: 2,
          pt: 1.5,
          borderTop: "1px solid #e5e7eb",
          gap: 1.5,
          flexDirection: "row",
        }}
      >
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={loading}
          sx={{
            flex: 1,
            py: 1.75,
            borderRadius: "10px",
            fontWeight: 600,
            fontSize: 16,
            textTransform: "none",
            bgcolor: "#f3f4f6",
            borderColor: "#e5e7eb",
            color: "#0f172a",
            "&:hover": {
              bgcolor: "#e5e7eb",
              borderColor: "#e5e7eb",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAccept}
          disabled={!agreed || loading}
          sx={{
            flex: 2,
            py: 1.75,
            borderRadius: "10px",
            fontWeight: 700,
            fontSize: 16,
            textTransform: "none",
            background: agreed ? theme.gradient : `${theme.primary}60`,
            color: "#fff",
            "&:hover": {
              background: agreed ? theme.gradient : `${theme.primary}60`,
              opacity: agreed ? 0.95 : 1,
            },
          }}
        >
          {loading ? "Saving..." : "I Agree & Continue"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
